import { ApiConfig } from "../../../../constants";
import React, { FC, useEffect, useState } from "react";
import ApiService from "services/api.service";
import Template from "types/Template";
import { SidePanelComponentProps } from "../FlowBuilderSidePanel";
import { MessageNodeData } from "pages/FlowBuilderv2/Nodes/NodeData";
import { MessageType } from "types/Workflow";
import Button, { ButtonType } from "components/Elements/Buttonv2";
import { useDispatch } from "react-redux";
import {
  setTemplateInlineCreator,
  TemplateInlineEditor,
} from "reducers/flow-builder.reducer";
import { useAppSelector } from "store/hooks";
import {
  PushBuilderData,
  PushPlatforms,
} from "pages/PushBuilder/PushBuilderContent";
import LockScreenIOS from "pages/PushBuilder/Badges/LockScreenIOS";
import LockScreenAndroid from "pages/PushBuilder/Badges/LockScreenAndroid";
import Select from "components/Elements/Selectv2";
import CogIcon from "@heroicons/react/24/outline/CogIcon";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import validator from "validator";
import {
  flowChannelsQuery,
  flowInternalRecipientGroupsQuery,
  flowTemplatesByTypeQuery,
} from "pages/FlowBuilderv2/flowBuilderQueries";
import EmailContentEditor from "./EmailContentEditor";
import MessageTextEditor from "./MessageTextEditor";
import useLeadStatsFeatureFlags from "hooks/useLeadStatsFeatureFlags";
import { useSidePanelReadOnly } from "../SidePanelReadOnlyContext";

const POLICY_AGENT_DYNAMIC_RECIPIENT_SOURCE = "policy_agent_email";

interface FlowConnectionOption {
  id: string;
  name: string;
  kind?: "email" | "slack" | "sms";
  provider?: string;
  messagingProfileId?: string;
  from?: string;
  sendingOptions?: {
    id: string;
    sendingEmail: string;
    sendingName?: string;
  }[];
  replyToOptions?: {
    id: string;
    replyToEmail: string;
    replyToName?: string;
  }[];
  isOAuth?: boolean;
}

interface TelnyxNumberAssignment {
  id: string;
  phoneNumber: string;
  messagingProfileId?: string | null;
  companyId?: string | null;
  agencyTagName?: string | null;
  label?: string | null;
  active: boolean;
  isDefault?: boolean;
  status?: string | null;
  sendCount?: number;
  lastSentAt?: string | null;
}

const MessageSettings: FC<
  SidePanelComponentProps<MessageNodeData> & {
    // The email preview dialog is launched from the side panel footer (between
    // Cancel and Save), so its open state is owned there and threaded down.
    emailPreviewOpen?: boolean;
    setEmailPreviewOpen?: (open: boolean) => void;
  }
> = ({
  nodeData,
  setNodeData,
  setIsError,
  showErrors,
  emailPreviewOpen,
  setEmailPreviewOpen,
}) => {
  const readOnly = useSidePanelReadOnly();
  const { flags } = useLeadStatsFeatureFlags();
  const templateType = nodeData.template?.type;
  const selectedTemplate = nodeData.template?.selected;

  const userData = useAppSelector((state) => state.auth.userData);

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // Seed from the React Query cache synchronously so the panel renders with
  // data on first paint instead of flashing empty while the async effect runs.
  const [templateList, setTemplateList] = useState<Template[]>(() => {
    if (!templateType) return [];
    return (
      queryClient.getQueryData<Template[]>(
        flowTemplatesByTypeQuery(templateType).queryKey
      ) ?? []
    );
  });
  const [connectionList, setConnectionList] = useState<FlowConnectionOption[]>(
    []
  );
  const [telnyxNumbers, setTelnyxNumbers] = useState<TelnyxNumberAssignment[]>(
    []
  );
  const [isLoadingTelnyxNumbers, setIsLoadingTelnyxNumbers] = useState(false);
  const [internalRecipientGroups, setInternalRecipientGroups] = useState<
    { id: string; name: string; recipientCount: number; enabled: boolean }[]
  >([]);
  const [additionalEmailInput, setAdditionalEmailInput] = useState("");
  const [additionalPhoneInput, setAdditionalPhoneInput] = useState("");
  const { nodes, templateInlineCreation } = useAppSelector(
    (state) => state.flowBuilder
  );

  // Email/SMS/Slack split content and delivery onto two tabs, like AI Response.
  const [activeTab, setActiveTab] = useState<"content" | "settings">("content");
  const [availablePlatformOptions, setAvailablePlatformOptions] = useState<
    { key: PushPlatforms | "All"; title: string }[]
  >([]);

  const normalizePhoneNumber = (value: string) => {
    const trimmed = value.trim();
    const stripped = trimmed.replace(/[^\d+]/g, "");
    return stripped.startsWith("+")
      ? `+${stripped.slice(1).replace(/\+/g, "")}`
      : stripped.replace(/\+/g, "");
  };
  const isValidPhoneNumber = (value: string) =>
    /^\+?[1-9]\d{6,14}$/.test(normalizePhoneNumber(value));

  const isSpecificEmail =
    flags.internalNotifications.enabled &&
    templateType === MessageType.EMAIL &&
    nodeData.recipientMode === "internal";
  const isSpecificSms =
    flags.internalNotifications.enabled &&
    templateType === MessageType.SMS &&
    nodeData.recipientMode === "internal";
  // "Notify management/agents": the message still goes to the contact, but
  // internal recipients are notified too. Only offered in customer mode.
  const alsoNotifyInternal =
    nodeData.recipientMode !== "internal" &&
    nodeData.alsoNotifyInternal === true;
  // "Notify management/agents" is offered on every channel (in customer mode).
  // Internal-only mode is still limited to email/SMS via the toggle below.
  const canAlsoNotifyInternal =
    flags.internalNotifications.enabled &&
    nodeData.recipientMode !== "internal";
  // Staff are notified by email for every channel except SMS (which copies them
  // by SMS). So the email recipient picker shows for internal-only email, and
  // for "notify management/agents" on any non-SMS channel.
  const showInternalEmailConfig =
    flags.internalNotifications.enabled &&
    ((templateType === MessageType.EMAIL &&
      nodeData.recipientMode === "internal") ||
      (alsoNotifyInternal && templateType !== MessageType.SMS));
  const showInternalSmsConfig =
    flags.internalNotifications.enabled &&
    templateType === MessageType.SMS &&
    (nodeData.recipientMode === "internal" || alsoNotifyInternal);
  const selectedInternalGroupIds = nodeData.internalRecipientGroupIds ?? [];
  const additionalEmails = nodeData.additionalEmails ?? [];
  const dynamicRecipientSources = nodeData.dynamicRecipientSources ?? [];
  const additionalPhoneNumbers = nodeData.additionalPhoneNumbers ?? [];
  const includesPolicyAgentRecipient = dynamicRecipientSources.includes(
    POLICY_AGENT_DYNAMIC_RECIPIENT_SOURCE
  );
  const hasSpecificEmailRecipient =
    selectedInternalGroupIds.length > 0 ||
    additionalEmails.some((email) => validator.isEmail(email)) ||
    includesPolicyAgentRecipient;
  const hasSpecificSmsRecipient =
    additionalPhoneNumbers.some(isValidPhoneNumber);
  const hasSpecificRecipient =
    (showInternalEmailConfig && hasSpecificEmailRecipient) ||
    (showInternalSmsConfig && hasSpecificSmsRecipient);
  const requiredConnectionKind =
    templateType === MessageType.EMAIL
      ? "email"
      : templateType === MessageType.SMS
      ? "sms"
      : templateType === MessageType.SLACK
      ? "slack"
      : undefined;
  const matchingConnections = requiredConnectionKind
    ? connectionList.filter(
        (connection) => connection.kind === requiredConnectionKind
      )
    : connectionList;
  const channelLabel =
    templateType === MessageType.EMAIL
      ? "email"
      : templateType === MessageType.SMS
      ? "SMS"
      : templateType === MessageType.SLACK
      ? "Slack"
      : "message";
  const smsSenderMode = nodeData.smsSenderMode ?? "connection";
  const selectedConnection = connectionList.find(
    (connection) => connection.id === nodeData.connectionId
  );
  const activeTelnyxNumbers = telnyxNumbers.filter(
    (number) =>
      number.active !== false &&
      (!selectedConnection?.messagingProfileId ||
        number.messagingProfileId === selectedConnection.messagingProfileId)
  );
  const telnyxAgencyLabels = Array.from(
    new Set(
      activeTelnyxNumbers
        .map((number) => number.agencyTagName || number.label)
        .filter((value): value is string => Boolean(value))
    )
  );
  const telnyxProfileMismatchCount = telnyxNumbers.filter(
    (number) =>
      number.active !== false &&
      selectedConnection?.messagingProfileId &&
      number.messagingProfileId !== selectedConnection.messagingProfileId
  ).length;

  useEffect(() => {
    const needsConnection = Boolean(requiredConnectionKind);
    if (templateType === MessageType.PUSH) {
      setIsError(
        !nodeData.template?.selected ||
          ((showInternalEmailConfig || showInternalSmsConfig) &&
            !hasSpecificRecipient)
      );
    } else {
      setIsError(
        !selectedTemplate?.id ||
          (needsConnection && !nodeData.connectionId) ||
          ((showInternalEmailConfig || showInternalSmsConfig) &&
            !hasSpecificRecipient)
      );
    }
  }, [
    templateType,
    selectedTemplate?.id,
    requiredConnectionKind,
    nodeData.connectionId,
    nodeData.recipientMode,
    showInternalEmailConfig,
    showInternalSmsConfig,
    hasSpecificRecipient,
    includesPolicyAgentRecipient,
  ]);

  useEffect(() => {
    (async () => {
      const {
        mailgunConnections,
        sendgridConnections,
        resendConnections,
        gmailConnections,
        office365Connections,
        slackInstallations,
        twilioConnections,
        telnyxConnections,
      } = await queryClient.ensureQueryData(flowChannelsQuery());

      setConnectionList([
        ...((mailgunConnections || []) as any[]).map((c: any) => ({
          ...c,
          kind: "email" as const,
          provider: "mailgun",
        })),
        ...((sendgridConnections || []) as any[]).map((c: any) => ({
          ...c,
          kind: "email" as const,
          provider: "sendgrid",
        })),
        ...((resendConnections || []) as any[]).map((c: any) => ({
          ...c,
          kind: "email" as const,
          provider: "resend",
        })),
        ...((gmailConnections || []) as any[]).map((c: any) => ({
          ...c,
          kind: "email" as const,
          provider: "gmail",
          isOAuth: true,
        })),
        ...((office365Connections || []) as any[]).map((c: any) => ({
          ...c,
          kind: "email" as const,
          provider: "office365",
          isOAuth: true,
        })),
        ...((slackInstallations || []) as any[]).map((s: any) => ({
          id: s.id,
          name:
            s.installation?.team?.name ||
            s.installation?.enterprise?.name ||
            s.id,
          kind: "slack" as const,
          provider: "slack",
          isOAuth: true,
        })),
        ...((twilioConnections || []) as any[]).map((c: any) => ({
          id: c.id,
          name: `Twilio: ${c.name || c.id}`,
          kind: "sms" as const,
          provider: "twilio",
        })),
        ...((telnyxConnections || []) as any[]).map((c: any) => ({
          ...c,
          id: c.id,
          name: `Telnyx: ${c.name || c.id}`,
          kind: "sms" as const,
          provider: "telnyx",
        })),
      ]);
    })();
  }, []);

  useEffect(() => {
    // Recipient groups feed the email recipient picker, which is used for
    // every channel except SMS (SMS copies staff by phone number).
    if (
      templateType === MessageType.SMS ||
      !flags.internalNotifications.enabled
    ) {
      setInternalRecipientGroups([]);
      return;
    }
    (async () => {
      try {
        const groups = await queryClient.ensureQueryData(
          flowInternalRecipientGroupsQuery()
        );
        setInternalRecipientGroups(groups);
      } catch {
        setInternalRecipientGroups([]);
      }
    })();
  }, [templateType, flags.internalNotifications.enabled, queryClient]);

  useEffect(() => {
    let cancelled = false;
    if (
      templateType !== MessageType.SMS ||
      selectedConnection?.provider !== "telnyx" ||
      !nodeData.connectionId
    ) {
      setTelnyxNumbers([]);
      setIsLoadingTelnyxNumbers(false);
      return;
    }

    setIsLoadingTelnyxNumbers(true);
    ApiService.get<TelnyxNumberAssignment[]>({
      url: "/workspaces/telnyx/phone-numbers",
      options: { params: { connectionId: nodeData.connectionId } },
    })
      .then(({ data }) => {
        if (!cancelled) setTelnyxNumbers(data || []);
      })
      .catch(() => {
        if (!cancelled) setTelnyxNumbers([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingTelnyxNumbers(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    templateType,
    nodeData.connectionId,
    selectedConnection?.provider,
    selectedConnection?.messagingProfileId,
  ]);

  // Default the connection when nothing is picked yet: for email prefer the
  // user's Gmail account, otherwise fall back to the only connection of the
  // matching kind. Never overrides an existing choice.
  useEffect(() => {
    if (nodeData.connectionId) return;
    const kind =
      templateType === MessageType.EMAIL
        ? "email"
        : templateType === MessageType.SMS
        ? "sms"
        : templateType === MessageType.SLACK
        ? "slack"
        : undefined;
    if (!kind) return;
    const conns = connectionList.filter((c) => c.kind === kind);
    if (conns.length === 0) return;
    const pick =
      (kind === "email" && conns.find((c) => c.provider === "gmail")) ||
      (conns.length === 1 ? conns[0] : undefined);
    if (pick) setNodeData({ ...nodeData, connectionId: pick.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionList, templateType, nodeData.connectionId]);

  const getAllTemplates = async (forceRefresh = false) => {
    if (!templateType) return;
    const templatesQuery = flowTemplatesByTypeQuery(templateType);
    // After an edit/save we need fresh rows, so drop the cached entry first;
    // on a normal open we serve the warmed cache instantly.
    if (forceRefresh)
      await queryClient.invalidateQueries({
        queryKey: templatesQuery.queryKey,
      });

    let list = await queryClient.ensureQueryData(templatesQuery);
    // The list endpoint caps at 100 rows, so the template a node already
    // points at can be missing from it once a workspace has more than 100
    // templates of this type. When that happens the dropdown can't show the
    // current selection and the node looks unconfigured. Fetch the selected
    // template by id and prepend it so it always appears as selected.
    const selectedId = selectedTemplate?.id;
    if (selectedId && !list.some((t) => t.id === selectedId)) {
      try {
        const { data: one } = await ApiService.get<Template>({
          url: `${ApiConfig.getAllTemplates}/${selectedId}`,
        });
        if (one?.id) list = [one, ...list];
      } catch {
        // If it can't be fetched, leave the list as-is — the dropdown just
        // won't pre-select, which is no worse than before.
      }
    }
    setTemplateList(list);
  };

  const handleTemplateInlineEdit = () => {
    const selectedNode = nodes.find((el) => el.selected);

    if (!selectedNode) {
      return;
    }
    const inlineCreator: TemplateInlineEditor = {
      selectedNode,
      type: templateType,
    };

    if (nodeData.template.selected?.id) {
      inlineCreator.templateId = nodeData.template.selected.id.toString();
    }

    dispatch(setTemplateInlineCreator(inlineCreator));
  };

  const countAvailablePlatforms = (data: PushBuilderData) => {
    const isAll = data.platform.Android && data.platform.iOS;

    const options: { key: PushPlatforms | "All"; title: string }[] = [];

    if (isAll) {
      options.push(
        {
          key: "All",
          title: "All",
        },
        {
          key: PushPlatforms.IOS,
          title: "iOS",
        },
        {
          key: PushPlatforms.ANDROID,
          title: "Android",
        }
      );
    } else if (data.platform.Android) {
      options.push({
        key: PushPlatforms.ANDROID,
        title: "Android",
      });
    } else if (data.platform.iOS) {
      options.push({
        key: PushPlatforms.IOS,
        title: "iOS",
      });
    }

    return options;
  };

  useEffect(() => {
    const selectedNode = nodes.find((el) => el.selected);

    if (
      !templateInlineCreation?.needsCallbackUpdate ||
      templateInlineCreation.selectedNode.id !== selectedNode?.id
    )
      return;

    const newPlatform =
      templateInlineCreation.needsCallbackUpdate.data.platform.Android &&
      templateInlineCreation.needsCallbackUpdate.data.platform.iOS
        ? "All"
        : templateInlineCreation.needsCallbackUpdate.data.platform.Android
        ? PushPlatforms.ANDROID
        : PushPlatforms.IOS;

    setAvailablePlatformOptions(
      countAvailablePlatforms(templateInlineCreation.needsCallbackUpdate.data)
    );

    setNodeData({
      ...nodeData,
      template: {
        ...nodeData.template,
        selected: {
          id: +templateInlineCreation.needsCallbackUpdate.id,
          name: templateInlineCreation.needsCallbackUpdate.name,
          pushBuilder: {
            ...templateInlineCreation.needsCallbackUpdate.data,
            selectedPlatform: newPlatform,
          },
        },
      },
    });
    dispatch(setTemplateInlineCreator(undefined));
  }, [templateInlineCreation?.needsCallbackUpdate]);

  useEffect(() => {
    getAllTemplates();
  }, [templateType]);

  useEffect(() => {
    if (selectedTemplate?.pushBuilder)
      setAvailablePlatformOptions(
        countAvailablePlatforms(selectedTemplate?.pushBuilder)
      );
  }, []);

  // The full template record (including its body) lives in templateList,
  // which getAllTemplates now guarantees contains the selected one.
  const selectedFull = templateList.find((t) => t.id === selectedTemplate?.id);

  if (!templateType) return <>Unknown template type!</>;

  // The selected template's plain-text body for SMS/Slack, used as the
  // starting point when the step hasn't edited its own copy yet.
  const smsSlackBody =
    templateType === MessageType.SMS
      ? selectedFull?.smsText || ""
      : templateType === MessageType.SLACK
      ? selectedFull?.slackMessage || ""
      : templateType === MessageType.WEBHOOK
      ? (selectedFull as any)?.webhookData?.body || ""
      : "";
  const manageInternalGroupsPath = selectedInternalGroupIds[0]
    ? `/settings?tab=INTERNAL_RECIPIENTS&groupId=${encodeURIComponent(
        selectedInternalGroupIds[0]
      )}`
    : "/settings?tab=INTERNAL_RECIPIENTS";

  const toggleInternalGroup = (groupId: string) => {
    const nextGroupIds = selectedInternalGroupIds.includes(groupId)
      ? selectedInternalGroupIds.filter((id) => id !== groupId)
      : [...selectedInternalGroupIds, groupId];
    setNodeData({ ...nodeData, internalRecipientGroupIds: nextGroupIds });
  };

  const addAdditionalEmail = () => {
    const email = additionalEmailInput.trim().toLowerCase();
    if (!validator.isEmail(email)) return;
    if (additionalEmails.map((item) => item.toLowerCase()).includes(email)) {
      setAdditionalEmailInput("");
      return;
    }
    setNodeData({
      ...nodeData,
      additionalEmails: [...additionalEmails, email],
    });
    setAdditionalEmailInput("");
  };

  const removeAdditionalEmail = (email: string) => {
    setNodeData({
      ...nodeData,
      additionalEmails: additionalEmails.filter((item) => item !== email),
    });
  };

  const togglePolicyAgentRecipient = () => {
    const nextSources = includesPolicyAgentRecipient
      ? dynamicRecipientSources.filter(
          (source) => source !== POLICY_AGENT_DYNAMIC_RECIPIENT_SOURCE
        )
      : [...dynamicRecipientSources, POLICY_AGENT_DYNAMIC_RECIPIENT_SOURCE];
    setNodeData({ ...nodeData, dynamicRecipientSources: nextSources });
  };

  const addAdditionalPhoneNumber = () => {
    const phoneNumber = normalizePhoneNumber(additionalPhoneInput);
    if (!isValidPhoneNumber(phoneNumber)) return;
    if (additionalPhoneNumbers.includes(phoneNumber)) {
      setAdditionalPhoneInput("");
      return;
    }
    setNodeData({
      ...nodeData,
      additionalPhoneNumbers: [...additionalPhoneNumbers, phoneNumber],
    });
    setAdditionalPhoneInput("");
  };

  const removeAdditionalPhoneNumber = (phoneNumber: string) => {
    setNodeData({
      ...nodeData,
      additionalPhoneNumbers: additionalPhoneNumbers.filter(
        (item) => item !== phoneNumber
      ),
    });
  };

  const templateSelect = (
    <div className="flex p-5 justify-between items-center">
      <div>Template</div>
      <div className="flex flex-col gap-[10px]">
        <select
          className="w-[200px] h-[32px] rounded-sm px-[12px] py-[4px] text-[14px] font-roboto leading-[22px]"
          value={selectedTemplate?.id ?? ""}
          id="template-select"
          onChange={(e) =>
            setNodeData({
              ...nodeData,
              template: {
                type: templateType,
                selected: {
                  id: +e.target.value,
                  name:
                    templateList.find(
                      (template) => template.id === +e.target.value
                    )?.name || "",
                },
              },
            })
          }
        >
          <option value="" disabled hidden>
            Select a template
          </option>
          {templateList.map((template) => (
            <option value={template.id} key={template.id}>
              {template.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const connectionRow = requiredConnectionKind ? (
    <div className="flex gap-2.5 p-5 justify-between items-start">
      <div className="pt-[5px]">Connection</div>
      <div className="flex w-[240px] flex-col gap-[10px]">
        {matchingConnections.length === 0 ? (
          <div className="rounded border border-themed-border bg-themed-background px-3 py-2 text-[12px] leading-5 text-themed-textSecondary">
            No {channelLabel} connection available. Connect one in{" "}
            <span className="font-medium">Settings &rarr; Channels</span> first,
            then come back here.
          </div>
        ) : (
          <>
            <Select
              className="w-[240px] min-h-[32px]"
              buttonClassName="w-[240px] min-h-[32px]"
              buttonInnerWrapperClassName="w-[240px] min-h-[32px]"
              value={nodeData.connectionId}
              onChange={(value) =>
                setNodeData({ ...nodeData, connectionId: value })
              }
              options={matchingConnections.map((connection) => ({
                key: connection.id,
                title: connection.name,
              }))}
              placeholder="Select a connection"
            />
            {showErrors && !nodeData.connectionId && (
              <div className="text-[12px] text-[#F43F5E]">
                Pick a {channelLabel} connection.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  ) : null;

  const smsSenderModeRow =
    templateType === MessageType.SMS ? (
      <div className="flex gap-2.5 p-5 justify-between items-start">
        <div className="pt-[5px]">SMS sender</div>
        <Select
          className="w-[240px] min-h-[32px]"
          buttonClassName="w-[240px] min-h-[32px]"
          buttonInnerWrapperClassName="w-[240px] min-h-[32px]"
          value={smsSenderMode}
          onChange={(value) =>
            setNodeData({
              ...nodeData,
              smsSenderMode: value === "agency" ? "agency" : "connection",
            })
          }
          options={[
            { key: "connection", title: "Fixed 10DLC pool" },
            { key: "agency", title: "Lead agency 10DLC pool" },
          ]}
        />
      </div>
    ) : null;

  const smsTelnyxPoolRow =
    templateType === MessageType.SMS &&
    selectedConnection?.provider === "telnyx" ? (
      <div className="flex gap-2.5 p-5 justify-between items-start border-t border-themed-border">
        <div className="pt-[5px]">10DLC pool</div>
        <div className="w-[240px] rounded border border-themed-border bg-themed-background">
          <div className="border-b border-themed-border px-3 py-2">
            <div className="text-[13px] leading-5 font-semibold text-themed-text">
              {activeTelnyxNumbers.length} active number
              {activeTelnyxNumbers.length === 1 ? "" : "s"}
            </div>
            <div className="text-[12px] leading-5 text-themed-textSecondary">
              {smsSenderMode === "agency"
                ? "Uses only a number assigned to the lead agency; sending stops if none is configured."
                : "Uses the selected Telnyx connection pool."}
            </div>
          </div>
          <div className="flex flex-col gap-2 px-3 py-2 text-[12px] leading-5">
            <div>
              <span className="text-themed-textSecondary">Connection: </span>
              <span className="text-themed-text">
                {selectedConnection.name}
              </span>
            </div>
            {selectedConnection.messagingProfileId && (
              <div className="break-all">
                <span className="text-themed-textSecondary">Profile: </span>
                <span className="text-themed-text">
                  {selectedConnection.messagingProfileId}
                </span>
              </div>
            )}
            {telnyxAgencyLabels.length > 0 && (
              <div>
                <span className="text-themed-textSecondary">Agency: </span>
                <span className="text-themed-text">
                  {telnyxAgencyLabels.join(", ")}
                </span>
              </div>
            )}
            {isLoadingTelnyxNumbers ? (
              <div className="text-themed-textSecondary">
                Loading numbers...
              </div>
            ) : activeTelnyxNumbers.length > 0 ? (
              <div className="flex max-h-[150px] flex-col gap-1 overflow-y-auto">
                {activeTelnyxNumbers.map((number) => (
                  <div
                    key={number.id}
                    className="flex items-center justify-between gap-2 rounded border border-themed-border bg-themed-card px-2 py-1"
                  >
                    <span className="text-themed-text">
                      {number.phoneNumber}
                    </span>
                    {number.isDefault && (
                      <span className="text-themed-textSecondary">default</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[#F43F5E]">
                No active Telnyx numbers match this messaging profile.
              </div>
            )}
            {telnyxProfileMismatchCount > 0 && (
              <div className="text-themed-textSecondary">
                {telnyxProfileMismatchCount} active number
                {telnyxProfileMismatchCount === 1 ? "" : "s"} on another profile
                excluded.
              </div>
            )}
          </div>
        </div>
      </div>
    ) : null;

  // Sending/reply-to options only exist on Mailgun/SendGrid/Resend connections.
  // Gmail/365 (OAuth) always send from the connected account, and with no
  // connection picked there's nothing to choose, so render nothing in those
  // cases.
  const emailSendingOptions = (() => {
    if (!selectedConnection || selectedConnection.isOAuth) return null;
    return (
      <>
        <div className="flex gap-2.5 p-5 justify-between items-center">
          <div>Sending option</div>
          <div className="flex flex-col gap-[10px]">
            <Select
              className="w-[200px] min-h-[32px]"
              buttonClassName="w-[200px] min-h-[32px]"
              buttonInnerWrapperClassName="w-[200px] min-h-[32px]"
              value={nodeData.sendingOptionId}
              onChange={(value) =>
                setNodeData({ ...nodeData, sendingOptionId: value })
              }
              options={
                nodeData.connectionId
                  ? selectedConnection?.sendingOptions?.map((option) => ({
                      key: option.id,
                      title: `${option.sendingEmail}${
                        option.sendingName ? ` <${option.sendingName}>` : ""
                      }`,
                    })) || []
                  : []
              }
              placeholder="Select an option"
            />
          </div>
        </div>
        <div className="flex gap-2.5 p-5 justify-between items-center">
          <div>Reply To Option</div>
          <div className="flex flex-col gap-[10px]">
            <Select
              className="w-[200px] min-h-[32px]"
              buttonClassName="w-[200px] min-h-[32px]"
              buttonInnerWrapperClassName="w-[200px] min-h-[32px]"
              value={nodeData.replyToOptionId}
              onChange={(value) =>
                setNodeData({ ...nodeData, replyToOptionId: value })
              }
              options={
                nodeData.connectionId
                  ? selectedConnection?.replyToOptions?.map((option: any) => ({
                      key: option.id,
                      title: `${option.replyToEmail}${
                        option.replyToName ? ` <${option.replyToName}>` : ""
                      }`,
                    })) || []
                  : []
              }
              placeholder="Select an option"
            />
          </div>
        </div>
      </>
    );
  })();

  const isTabbedChannel =
    templateType === MessageType.EMAIL ||
    templateType === MessageType.SMS ||
    templateType === MessageType.SLACK ||
    templateType === MessageType.WEBHOOK;
  const settingsHasErrors =
    (Boolean(requiredConnectionKind) && !nodeData.connectionId) ||
    ((showInternalEmailConfig || showInternalSmsConfig) &&
      !hasSpecificRecipient);

  return (
    <div
      className={`flex flex-col gap-[10px] h-full${
        readOnly ? " pointer-events-none select-none" : ""
      }`}
    >
      {isTabbedChannel ? (
        <div className="font-inter font-normal text-[14px] leading-[22px] flex flex-col h-full">
          <div
            className={`flex gap-2 border-b border-themed-borderSecondary px-5 pt-2${
              readOnly ? " pointer-events-auto" : ""
            }`}
          >
            <button
              type="button"
              onClick={() => setActiveTab("content")}
              className={`px-3 py-2 text-[13px] border-b-2 -mb-px ${
                activeTab === "content"
                  ? "border-[#7C3AED] text-[#7C3AED] font-semibold"
                  : "border-transparent text-themed-text hover:text-[#7C3AED]"
              }`}
            >
              Content
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("settings")}
              className={`px-3 py-2 text-[13px] border-b-2 -mb-px flex items-center gap-1 ${
                activeTab === "settings"
                  ? "border-[#7C3AED] text-[#7C3AED] font-semibold"
                  : "border-transparent text-themed-text hover:text-[#7C3AED]"
              }`}
            >
              Settings
              {settingsHasErrors && (
                <span className="w-[6px] h-[6px] rounded-full bg-[#F43F5E]" />
              )}
            </button>
          </div>
          {activeTab === "content" ? (
            <div className="flex-1 min-h-0 flex flex-col">
              {templateSelect}
              {templateType === MessageType.EMAIL && (
                <EmailContentEditor
                  key={selectedTemplate?.id ?? "no-template"}
                  templateSubject={selectedFull?.subject}
                  templateText={selectedFull?.text}
                  override={nodeData.emailOverride}
                  onChange={(emailOverride) =>
                    setNodeData({ ...nodeData, emailOverride })
                  }
                  previewOpen={emailPreviewOpen}
                  onPreviewClose={() => setEmailPreviewOpen?.(false)}
                />
              )}
              {(templateType === MessageType.SMS ||
                templateType === MessageType.SLACK ||
                templateType === MessageType.WEBHOOK) && (
                <MessageTextEditor
                  key={selectedTemplate?.id ?? "no-template"}
                  label={
                    templateType === MessageType.WEBHOOK
                      ? "Request body"
                      : "Message"
                  }
                  value={nodeData.textOverride ?? smsSlackBody}
                  onChange={(text) =>
                    setNodeData({ ...nodeData, textOverride: text })
                  }
                  placeholder={
                    templateType === MessageType.WEBHOOK
                      ? "Request body sent to the webhook URL"
                      : "Type the message contacts will receive"
                  }
                  channel={templateType === MessageType.SMS ? "sms" : "slack"}
                />
              )}
            </div>
          ) : (
            <div className="flex flex-col">
              {flags.internalNotifications.enabled && (
                <>
                  {(templateType === MessageType.EMAIL ||
                    templateType === MessageType.SMS) && (
                    <div className="flex gap-2.5 p-5 justify-between items-center">
                      <div>Recipient</div>
                      <div className="flex rounded border border-themed-border overflow-hidden">
                        <button
                          type="button"
                          className={`px-3 py-1 text-[13px] ${
                            nodeData.recipientMode !== "internal"
                              ? "bg-[#EEF2FF] text-[#4338CA] font-semibold"
                              : "text-themed-text"
                          }`}
                          onClick={() =>
                            setNodeData({
                              ...nodeData,
                              recipientMode: "customer",
                            })
                          }
                        >
                          Lead/customer
                        </button>
                        <button
                          type="button"
                          className={`px-3 py-1 text-[13px] border-l border-themed-border ${
                            isSpecificEmail || isSpecificSms
                              ? "bg-[#EEF2FF] text-[#4338CA] font-semibold"
                              : "text-themed-text"
                          }`}
                          onClick={() =>
                            setNodeData({
                              ...nodeData,
                              recipientMode: "internal",
                              internalRecipientGroupIds:
                                nodeData.internalRecipientGroupIds ?? [],
                              additionalEmails: nodeData.additionalEmails ?? [],
                              dynamicRecipientSources:
                                nodeData.dynamicRecipientSources ?? [],
                              additionalPhoneNumbers:
                                nodeData.additionalPhoneNumbers ?? [],
                            })
                          }
                        >
                          {templateType === MessageType.SMS
                            ? "Specific phones"
                            : "Specific people"}
                        </button>
                      </div>
                    </div>
                  )}
                  {canAlsoNotifyInternal && (
                    <div className="px-5 pt-5 pb-5 border-b border-themed-border">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={alsoNotifyInternal}
                          onChange={() =>
                            setNodeData({
                              ...nodeData,
                              alsoNotifyInternal: !alsoNotifyInternal,
                              internalRecipientGroupIds:
                                nodeData.internalRecipientGroupIds ?? [],
                              additionalEmails: nodeData.additionalEmails ?? [],
                              dynamicRecipientSources:
                                nodeData.dynamicRecipientSources ?? [],
                              additionalPhoneNumbers:
                                nodeData.additionalPhoneNumbers ?? [],
                            })
                          }
                        />
                        <span>Notify management/agents</span>
                      </label>
                      <div className="text-[12px] text-themed-textSecondary mt-1">
                        {templateType === MessageType.EMAIL ||
                        templateType === MessageType.SMS
                          ? `Also send a copy of this ${channelLabel} to internal recipients, in addition to the contact.`
                          : "Also email internal recipients about this message, in addition to the contact."}
                      </div>
                    </div>
                  )}
                  {showInternalEmailConfig && (
                    <div className="px-5 pb-5 flex flex-col gap-3 border-b border-themed-border">
                      <div className="flex items-center justify-between">
                        <div className="font-semibold">Recipient groups</div>
                        <Link
                          to={manageInternalGroupsPath}
                          target="_blank"
                          className="text-[#6366F1] text-[13px]"
                        >
                          Manage groups
                        </Link>
                      </div>
                      <div className="flex flex-col gap-2 max-h-[180px] overflow-y-auto">
                        {internalRecipientGroups.length ? (
                          internalRecipientGroups.map((group) => (
                            <label
                              key={group.id}
                              className={`flex items-center justify-between gap-3 rounded border border-themed-border px-3 py-2 ${
                                group.enabled ? "" : "opacity-50"
                              }`}
                            >
                              <span className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={selectedInternalGroupIds.includes(
                                    group.id
                                  )}
                                  onChange={() => toggleInternalGroup(group.id)}
                                  disabled={!group.enabled}
                                />
                                <span>{group.name}</span>
                              </span>
                              <span className="text-[12px] text-themed-textSecondary">
                                {group.recipientCount} enabled
                              </span>
                            </label>
                          ))
                        ) : (
                          <div className="text-[13px] text-themed-textSecondary">
                            No internal recipient groups configured.
                          </div>
                        )}
                      </div>
                      <label className="flex items-center justify-between gap-3 rounded border border-themed-border px-3 py-2">
                        <span className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={includesPolicyAgentRecipient}
                            onChange={togglePolicyAgentRecipient}
                          />
                          <span>Policy producer</span>
                        </span>
                        <span className="text-[12px] text-themed-textSecondary">
                          From policy agent ID
                        </span>
                      </label>
                      <div className="flex flex-col gap-2">
                        <div className="font-semibold">
                          Specific email addresses
                        </div>
                        <div className="flex gap-2">
                          <input
                            className="w-full border border-themed-border rounded px-3 py-1 text-[14px]"
                            value={additionalEmailInput}
                            onChange={(e) =>
                              setAdditionalEmailInput(e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addAdditionalEmail();
                              }
                            }}
                            placeholder="name@example.com"
                          />
                          <Button
                            type={ButtonType.SECONDARY}
                            disabled={
                              !validator.isEmail(additionalEmailInput.trim())
                            }
                            onClick={addAdditionalEmail}
                          >
                            Add
                          </Button>
                        </div>
                        {additionalEmails.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {additionalEmails.map((email) => (
                              <button
                                type="button"
                                key={email}
                                className="rounded border border-themed-border px-2 py-1 text-[12px]"
                                onClick={() => removeAdditionalEmail(email)}
                              >
                                {email} x
                              </button>
                            ))}
                          </div>
                        )}
                        {showErrors && !hasSpecificEmailRecipient && (
                          <div className="text-[#F43F5E] text-[13px]">
                            Select at least one recipient group, email address,
                            or dynamic recipient.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {showInternalSmsConfig && (
                    <div className="px-5 pb-5 flex flex-col gap-3 border-b border-themed-border">
                      <div className="flex flex-col gap-2">
                        <div className="font-semibold">
                          Specific phone numbers
                        </div>
                        <div className="flex gap-2">
                          <input
                            className="w-full border border-themed-border rounded px-3 py-1 text-[14px]"
                            value={additionalPhoneInput}
                            onChange={(e) =>
                              setAdditionalPhoneInput(e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addAdditionalPhoneNumber();
                              }
                            }}
                            placeholder="+15551234567"
                          />
                          <Button
                            type={ButtonType.SECONDARY}
                            disabled={!isValidPhoneNumber(additionalPhoneInput)}
                            onClick={addAdditionalPhoneNumber}
                          >
                            Add
                          </Button>
                        </div>
                        {additionalPhoneNumbers.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {additionalPhoneNumbers.map((phoneNumber) => (
                              <button
                                type="button"
                                key={phoneNumber}
                                className="rounded border border-themed-border px-2 py-1 text-[12px]"
                                onClick={() =>
                                  removeAdditionalPhoneNumber(phoneNumber)
                                }
                              >
                                {phoneNumber} x
                              </button>
                            ))}
                          </div>
                        )}
                        {showErrors && !hasSpecificSmsRecipient && (
                          <div className="text-[#F43F5E] text-[13px]">
                            Add at least one valid phone number.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
              {connectionRow}
              {smsSenderModeRow}
              {smsTelnyxPoolRow}
              {templateType === MessageType.EMAIL && emailSendingOptions}
            </div>
          )}
        </div>
      ) : userData.pushPlatforms &&
        Object.keys(userData.pushPlatforms).length === 0 ? (
        <div className="w-full p-5">
          <div className="flex w-full gap-[5px] items-center">
            <CogIcon className="min-w-[24px] min-h-[24px] max-w-[24px] max-h-[24px] text-themed-text" />
            <span className="text-base font-inter font-medium text-themed-text">
              Setup required for Push
            </span>
          </div>
          <div className="text-sm my-[10px] font-inter text-themed-text">
            Push supports iOS and Android devices. To enable this feature in
            your workflow, a brief setup is required for correct delivery.
          </div>
          <Link to="/settings/push" target="_blank">
            <Button
              className="w-full"
              type={ButtonType.PRIMARY}
              onClick={() => {}}
            >
              Go To Push Setup
            </Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="p-5 flex flex-col gap-[10px]">
            <div className="text-themed-text font-inter text-[14px] leading-[22px] font-semibold">
              Push
            </div>

            {selectedTemplate?.pushBuilder ? (
              <div className="w-full border border-themed-border rounded overflow-hidden">
                <div className="px-[10px] py-2 bg-themed-background flex justify-between">
                  <div className="max-w-full text-themed-text text-[14px] font-inter font-semibold leading-[22px] overflow-hidden text-ellipsis">
                    {selectedTemplate.name}
                  </div>
                  <div className="min-w-[52px] max-w-[52px] flex justify-between items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      className="cursor-pointer"
                      onClick={handleTemplateInlineEdit}
                    >
                      <g clipPath="url(#clip0_267_6950)">
                        <path
                          d="M3.45872 12.2841C3.49443 12.2841 3.53015 12.2805 3.56586 12.2752L6.56943 11.7484C6.60515 11.7412 6.63908 11.7252 6.66408 11.6984L14.2337 4.12874C14.2503 4.11222 14.2634 4.0926 14.2724 4.071C14.2813 4.0494 14.2859 4.02624 14.2859 4.00285C14.2859 3.97946 14.2813 3.95631 14.2724 3.9347C14.2634 3.9131 14.2503 3.89348 14.2337 3.87696L11.2659 0.907316C11.2319 0.873387 11.1873 0.85553 11.1391 0.85553C11.0909 0.85553 11.0462 0.873387 11.0123 0.907316L3.44265 8.47696C3.41586 8.50374 3.39979 8.53589 3.39265 8.5716L2.86586 11.5752C2.84849 11.6708 2.8547 11.7693 2.88395 11.862C2.91319 11.9547 2.9646 12.0389 3.03372 12.1073C3.15158 12.2216 3.29979 12.2841 3.45872 12.2841ZM4.66229 9.16982L11.1391 2.69482L12.448 4.00374L5.97122 10.4787L4.38372 10.7591L4.66229 9.16982ZM14.5712 13.7841H1.42836C1.11229 13.7841 0.856934 14.0395 0.856934 14.3555V14.9984C0.856934 15.077 0.921219 15.1412 0.999791 15.1412H14.9998C15.0784 15.1412 15.1426 15.077 15.1426 14.9984V14.3555C15.1426 14.0395 14.8873 13.7841 14.5712 13.7841Z"
                          fill="#111827"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_267_6950">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      className="cursor-pointer"
                      onClick={() =>
                        setNodeData({
                          ...nodeData,
                          template: {
                            ...nodeData.template,
                            selected: undefined,
                          },
                        })
                      }
                    >
                      <path
                        d="M5.28544 2.14124H5.14258C5.22115 2.14124 5.28544 2.07696 5.28544 1.99839V2.14124H10.714V1.99839C10.714 2.07696 10.7783 2.14124 10.8569 2.14124H10.714V3.42696H11.9997V1.99839C11.9997 1.36803 11.4872 0.85553 10.8569 0.85553H5.14258C4.51222 0.85553 3.99972 1.36803 3.99972 1.99839V3.42696H5.28544V2.14124ZM14.2854 3.42696H1.71401C1.39794 3.42696 1.14258 3.68232 1.14258 3.99839V4.56982C1.14258 4.64839 1.20686 4.71267 1.28544 4.71267H2.36401L2.80508 14.052C2.83365 14.6609 3.33722 15.1412 3.94615 15.1412H12.0533C12.664 15.1412 13.1658 14.6627 13.1944 14.052L13.6354 4.71267H14.714C14.7926 4.71267 14.8569 4.64839 14.8569 4.56982V3.99839C14.8569 3.68232 14.6015 3.42696 14.2854 3.42696ZM11.9158 13.8555H4.08365L3.65151 4.71267H12.3479L11.9158 13.8555Z"
                        fill="#111827"
                      />
                    </svg>
                  </div>
                </div>
                <div className="p-[10px]">
                  {selectedTemplate.pushBuilder.platform.iOS && (
                    <div
                      className={`${
                        selectedTemplate.pushBuilder.platform.iOS &&
                        selectedTemplate.pushBuilder.platform.Android &&
                        "mb-[20px]"
                      }`}
                    >
                      <div className="text-[12px] font-inter text-themed-text mb-[10px]">
                        iOS
                      </div>
                      <LockScreenIOS
                        settings={selectedTemplate.pushBuilder.settings.iOS}
                      />
                    </div>
                  )}
                  {selectedTemplate.pushBuilder.platform.Android && (
                    <div>
                      <div className="text-[12px] rounded- font-inter text-themed-text mb-[10px]">
                        Android
                      </div>
                      <LockScreenAndroid
                        settings={selectedTemplate.pushBuilder.settings.Android}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <Button
                type={ButtonType.PRIMARY}
                onClick={handleTemplateInlineEdit}
              >
                Add
              </Button>
            )}
          </div>
          <div className="w-full border-themed-border border-t-[1px] !my-[-10px]" />
          {nodeData.template.selected?.pushBuilder?.selectedPlatform && (
            <div className="p-5 flex flex-col gap-[10px]">
              <div className="text-themed-text font-inter text-[14px] leading-[22px] font-semibold">
                Platform
              </div>
              <Select
                value={nodeData.template.selected.pushBuilder.selectedPlatform}
                options={availablePlatformOptions}
                onChange={(key) =>
                  nodeData.template.selected?.pushBuilder &&
                  setNodeData({
                    ...nodeData,
                    template: {
                      ...nodeData.template,
                      selected: {
                        ...nodeData.template.selected,
                        pushBuilder: {
                          ...nodeData.template.selected?.pushBuilder,
                          selectedPlatform: key,
                        },
                      },
                    },
                  })
                }
              />
              {availablePlatformOptions.length === 1 && (
                <div className="text-[12px] font-inter leading-5 text-themed-textSecondary">
                  The current push is set exclusively for iOS devices
                </div>
              )}
            </div>
          )}
        </>
      )}
      {showErrors && !selectedTemplate && (
        <span className="px-5 mt-[10px] font-inter font-normal text-[14px] leading-[22px] text-[#F43F5E]">
          No template is selected
        </span>
      )}
    </div>
  );
};

export default MessageSettings;
